import { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

/**
 * Compute the growth schedule month-by-month, keep yearly snapshots.
 *
 * Nominal:  FV(m) = FV(m-1) * (1 + r) + PMT      with r = annualRate/12 (as decimal)
 * Real:     FV_real(m) = FV(m) / (1 + i)^m       with i = inflation/12 (as decimal)
 * Contrib:  simple sum of principal + monthly contributions
 */
function computeSchedule({ initial, monthly, annualRate, inflation, years }) {
  const r = (annualRate || 0) / 100 / 12;
  const i = (inflation || 0) / 100 / 12;
  const months = Math.max(1, Math.round((years || 0) * 12));

  const points = [];
  let nominal = Number(initial) || 0;
  let contributed = Number(initial) || 0;

  // Snapshot at year 0
  points.push({ year: 0, nominal, real: nominal, contributed });

  for (let m = 1; m <= months; m++) {
    nominal = nominal * (1 + r) + (Number(monthly) || 0);
    contributed += Number(monthly) || 0;
    if (m % 12 === 0) {
      const real = i > 0 ? nominal / Math.pow(1 + i, m) : nominal;
      points.push({ year: m / 12, nominal, real, contributed });
    }
  }
  return points;
}

const fmtCurrency = (n, currency = 'GBP') => {
  if (n === undefined || n === null || !isFinite(n)) return '-';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
};

function CompoundInterestModal({ isOpen, onClose }) {
  const [form, setForm] = useState({
    initial: 10000,
    monthly: 500,
    annualRate: 7,
    inflation: 2,
    years: 20,
    currency: 'GBP',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'currency' ? value : value === '' ? '' : Number(value),
    }));
  };

  const schedule = useMemo(() => computeSchedule(form), [form]);

  const final = schedule[schedule.length - 1] || { nominal: 0, real: 0, contributed: 0 };
  const interestEarned = final.nominal - final.contributed;

  const chartData = useMemo(
    () => ({
      labels: schedule.map((p) => `Y${p.year}`),
      datasets: [
        {
          label: 'Nominal value',
          data: schedule.map((p) => p.nominal),
          borderColor: '#00d4ff',
          backgroundColor: 'rgba(0,212,255,0.12)',
          fill: true,
          tension: 0.25,
          pointRadius: 2,
        },
        {
          label: 'Real value (inflation-adjusted)',
          data: schedule.map((p) => p.real),
          borderColor: '#c084fc',
          backgroundColor: 'rgba(192,132,252,0.08)',
          fill: false,
          tension: 0.25,
          borderDash: [5, 4],
          pointRadius: 2,
        },
        {
          label: 'Total contributed',
          data: schedule.map((p) => p.contributed),
          borderColor: '#7a99b8',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.1,
          borderDash: [2, 3],
          pointRadius: 0,
        },
      ],
    }),
    [schedule],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#dff0ff',
            font: { size: 11 },
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmtCurrency(ctx.parsed.y, form.currency)}`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: '#7a99b8' },
          grid: { color: 'rgba(122,153,184,0.1)' },
        },
        y: {
          ticks: {
            color: '#7a99b8',
            callback: (v) => fmtCurrency(v, form.currency),
          },
          grid: { color: 'rgba(122,153,184,0.1)' },
        },
      },
    }),
    [form.currency],
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal calc-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '820px', width: '95vw' }}
      >
        <div className="modal-header">
          <span className="modal-title">Compound Interest Calculator</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Inputs */}
          <div className="responsive-grid" style={{ '--col-min': '160px' }}>
            <div className="form-group">
              <label>Initial capital</label>
              <input
                type="number"
                inputMode="decimal"
                name="initial"
                value={form.initial}
                onChange={handleChange}
                min="0"
                step="100"
              />
            </div>
            <div className="form-group">
              <label>Monthly contribution</label>
              <input
                type="number"
                inputMode="decimal"
                name="monthly"
                value={form.monthly}
                onChange={handleChange}
                min="0"
                step="50"
              />
            </div>
            <div className="form-group">
              <label>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange}>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="responsive-grid" style={{ '--col-min': '160px', marginTop: '1rem' }}>
            <div className="form-group">
              <label>Annual return (%)</label>
              <input
                type="number"
                inputMode="decimal"
                name="annualRate"
                value={form.annualRate}
                onChange={handleChange}
                step="0.1"
              />
            </div>
            <div className="form-group">
              <label>Annual inflation (%)</label>
              <input
                type="number"
                inputMode="decimal"
                name="inflation"
                value={form.inflation}
                onChange={handleChange}
                step="0.1"
              />
            </div>
            <div className="form-group">
              <label>Years</label>
              <input
                type="number"
                inputMode="numeric"
                name="years"
                value={form.years}
                onChange={handleChange}
                min="1"
                max="60"
                step="1"
              />
            </div>
          </div>

          {/* KPIs */}
          <div
            className="responsive-grid"
            style={{ '--col-min': '140px', gap: '0.75rem', margin: '1rem 0' }}
          >
            <KpiBox label="Total contributed" value={fmtCurrency(final.contributed, form.currency)} />
            <KpiBox
              label="Interest earned"
              value={fmtCurrency(interestEarned, form.currency)}
              tone="pos"
            />
            <KpiBox
              label="Final (nominal)"
              value={fmtCurrency(final.nominal, form.currency)}
              highlight
            />
            <KpiBox
              label="Final (real)"
              value={fmtCurrency(final.real, form.currency)}
              tone="muted"
            />
          </div>

          {/* Chart */}
          <div className="calc-chart-wrapper">
            <Line data={chartData} options={chartOptions} />
          </div>

          <p
            style={{
              color: 'var(--text-muted)',
              fontSize: '0.75rem',
              marginTop: '0.75rem',
              textAlign: 'center',
            }}
          >
            Real value discounts nominal value by cumulative inflation. Returns are compounded
            monthly.
          </p>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiBox({ label, value, tone, highlight }) {
  const colorMap = {
    pos: 'var(--green)',
    neg: 'var(--red)',
    muted: 'var(--text-muted)',
  };
  const color = colorMap[tone] || 'var(--text)';
  return (
    <div className={`calc-kpi${highlight ? ' calc-kpi-hl' : ''}`}>
      <div className="calc-kpi-label">{label}</div>
      <div className="calc-kpi-value" style={{ color }}>{value}</div>
    </div>
  );
}

export default CompoundInterestModal;
